import { Fragment, FC, useEffect, useRef } from 'react';
import { Downloading } from 'types/Downloading';
import { ModalBasic } from 'shared/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { cancelProcessing, closeModal, getDownloadingState } from 'modules/downloading';
import { getScenes, getToolsDetails } from 'modules/scenes';
import { Box, Button, Close, Flex, Progress, SxStyleProp, Text } from 'theme-ui';
import { useTranslator } from '@picsart/rc/services/localization';
import { DownloadPreview } from './downloadingModal/DownloadPreview';
import { SceneType } from 'types/Scene';
import analytics from 'utils/analytics';
import { DownloadButton } from 'shared/DownloadButton';
import { last } from 'ramda';
import { createNewProjectFile, saveProjectFile } from 'modules/projectFles';
import { setOneTimePaymentReset } from 'modules/oneTimePayment';
import { ProcessingTaskStatusStatusEnum } from 'api';

const modalContainerStyles: SxStyleProp = {
  maxWidth: 1,
  justifyContent: 'center',
  cursor: 'default',
  width: 404
};

const modalContentStyles: SxStyleProp = {
  position: 'relative',
  width: '100%',
  px: '44px',
  py: 6,
  bg: 'background',
  textAlign: 'center',
  color: 'grey4',
  borderRadius: 'modal'
};

const closeStyle: SxStyleProp = {
  position: 'absolute',
  top: '12px',
  right: '12px',
  outline: 'none'
};

type Props = Omit<Downloading, 'taskId'> & {
  close: () => void;
  src: string;
  handleCancel: () => void;
  previewType: SceneType;
  handleDownload: () => void;
  createNewProject: () => void;
};

export const DownloadingModalBase: FC<Props> = ({
  modalShown,
  progress,
  rasterisationProgress,
  link,
  status,
  close,
  src,
  previewType,
  handleCancel,
  handleDownload,
  createNewProject
}) => {
  const translator = useTranslator();
  const downloadBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (status === ProcessingTaskStatusStatusEnum.Done) {
      downloadBtnRef.current?.click();
    }
  }, [status]);

  let titleMessage = 'Video_is_being_processed';

  if (status === ProcessingTaskStatusStatusEnum.Error) {
    titleMessage = 'Oops…';
  } else if (status === ProcessingTaskStatusStatusEnum.Done) {
    titleMessage = 'Hurray';
  }

  const totalProgress = progress * 0.8 + rasterisationProgress * 0.2;

  return (
    status && (
      <ModalBasic closeHandler={close} opened={modalShown}>
        <Flex data-test="modal">
          <Flex sx={modalContainerStyles}>
            <Box sx={modalContentStyles}>
              <Flex
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  mx: 4
                }}
              >
                <Text sx={{ color: 'text' }} variant="popupHeading">
                  {translator(titleMessage)}
                </Text>
                {close && <Close sx={closeStyle} onClick={close} data-test="close" />}
              </Flex>
              <Flex sx={{ flexDirection: 'column' }}>
                {(status === ProcessingTaskStatusStatusEnum.Running ||
                  status === ProcessingTaskStatusStatusEnum.Queued) && (
                  <Fragment>
                    <Text sx={{ minHeight: '42px' }} variant="modalBody">
                      {translator('Please_wait._You_will_be_able_to_download_it_very soon…')}
                    </Text>
                    <DownloadPreview type={previewType} src={src} />
                    <Progress mt="24px" mb="8px" value={totalProgress} max={100} />
                    <Text color="primary" variant="medium">
                      {Math.round(totalProgress)}%
                    </Text>
                    <Button
                      variant="clean"
                      sx={{
                        marginTop: '26px',
                        alignSelf: 'center'
                      }}
                      onClick={handleCancel}
                      data-test="cancel"
                    >
                      {translator('Cancel')}
                    </Button>
                  </Fragment>
                )}

                {status === ProcessingTaskStatusStatusEnum.Done && (
                  <Fragment>
                    <Text sx={{ mb: '20px' }} variant="modalBody">
                      {translator('Your video is successfully downloaded')}
                    </Text>
                    <Flex
                      sx={{
                        justifyContent: 'center',
                        mb: '20px'
                      }}
                    >
                      <img
                        src="https://cdn140.picsart.com/35516783934708465149.png"
                        alt="#"
                        width="128px"
                        height="112px"
                      />
                    </Flex>
                    <Flex
                      sx={{
                        button: {
                          justifyContent: 'center',
                          flexGrow: 1,
                          padding: '10px 0'
                        }
                      }}
                    >
                      <Button
                        onClick={close}
                        variant="clean"
                        data-test="continueEditing"
                        sx={{ whiteSpace: 'nowrap', mr: 4, color: 'grey7' }}
                      >
                        {translator('Continue Editing')}
                      </Button>
                      <Button
                        onClick={createNewProject}
                        variant="primary"
                        data-test="createNew"
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {translator('Create New')}
                      </Button>
                    </Flex>
                    <Flex
                      sx={{
                        bg: 'grey2',
                        p: 4,
                        borderRadius: 'button',
                        mt: 4
                      }}
                    >
                      <Text variant="body" sx={{ fontSize: 1, mb: 0 }}>
                        {translator('If your download didn’t start automatically,')}{' '}
                        <DownloadButton
                          ref={downloadBtnRef}
                          href={link}
                          download={last(link.split('/'))}
                          onClick={handleDownload}
                        >
                          {translator('click here')}
                        </DownloadButton>
                      </Text>
                    </Flex>
                  </Fragment>
                )}
              </Flex>
            </Box>
          </Flex>
        </Flex>
        )
      </ModalBasic>
    )
  );
};

const DownloadingModal = () => {
  const downloadingState = useSelector(getDownloadingState);
  const dispatch = useDispatch();
  const toolDetails = useSelector(getToolsDetails);
  const handleClose =
    downloadingState.status !== ProcessingTaskStatusStatusEnum.Running &&
    downloadingState.status !== ProcessingTaskStatusStatusEnum.Queued
      ? () => {
          dispatch(closeModal());
          dispatch(saveProjectFile());
        }
      : null;

  const createNewProject = () => {
    handleClose();
    dispatch(createNewProjectFile());
  };

  const handleCancel = () => {
    dispatch(cancelProcessing());
  };

  const scenes = useSelector(getScenes);

  const scene = scenes.find(item => item.type !== SceneType.EMPTY);
  if (!scene) return null;
  const { src: localSrc, type } = scene;

  const handleDownload = () => {
    const videoInfo = downloadingState.videoInfo;
    const resolution = videoInfo
      ? `${videoInfo.targetSettings.width}x${videoInfo.targetSettings.height}`
      : '';
    analytics.download({ resolution, tool_details: toolDetails });
    dispatch(setOneTimePaymentReset());
  };
  // TODO: зачем это здесь? Внутри DownloadingModalBase также есть проверка на ProcessingTaskStatusStatusEnum.Error
  if (downloadingState.status === ProcessingTaskStatusStatusEnum.Error) return null;

  return (
    <DownloadingModalBase
      {...downloadingState}
      close={handleClose}
      src={localSrc}
      previewType={type}
      handleCancel={handleCancel}
      handleDownload={handleDownload}
      createNewProject={createNewProject}
    />
  );
};

export default DownloadingModal;
